import ApiClient from "@/domain/core/utils/apiClient";
import client_gateway from "@/services/clients/client_gateway";
import client_core from "@/services/clients/client_core";
import downloader from "@/domain/core/utils/downloader";

const gatewayClient = new ApiClient(client_gateway)
const coreClient = new ApiClient(client_core)

const endpoints = {
    // Ecommerce endpoints
    orders: '/dashboard/ecommerce/orders/',
    orderDetail: '/dashboard/ecommerce/orders/{id}/',
    orderPatch: '/dashboard/ecommerce/orders/{id}/',
    exportCsv: '/dashboard/ecommerce/orders/export/',

    // Edge orders media endpoint (Core)
    edgeOrderMedia: '/dashboard/v2/edge-orders/{picaServiceId}/get_media/',

    // Media download endpoint (Core)
    downloadMedia: '/dashboard/v2/edge-orders/{picaServiceId}/download_media/'
}

export default {
    // Orders
    async fetchOrders(picaServiceId, params) {
        params = { ...params, pica_service_id: picaServiceId }

        // Process date range filters
        if (params.created_at_range) {
            const range = params.created_at_range
            if (range.start) params.created_at__gte = range.start
            if (range.end) params.created_at__lte = range.end
            delete params.created_at_range
        }

        if (params.paid_at_range) {
            const range = params.paid_at_range
            if (range.start) params.paid_at__gte = range.start
            if (range.end) params.paid_at__lte = range.end
            delete params.paid_at_range
        }

        // Remove null/empty values
        const cleanParams = Object.keys(params).reduce((acc, key) => {
            if (params[key] !== null && params[key] !== '') {
                acc[key] = params[key]
            }
            return acc
        }, {})

        return await gatewayClient.get(endpoints.orders, cleanParams)
    },

    async fetchOrderDetail(orderId) {
        const endpoint = endpoints.orderDetail.replace('{id}', orderId)
        return await gatewayClient.get(endpoint)
    },

    async updateOrderStatus(orderId, status) {
        const endpoint = endpoints.orderPatch.replace('{id}', orderId)
        return await gatewayClient.patch(endpoint, { status })
    },

    async exportOrdersCsv(picaServiceId, params) {
        // Add pica_service_id to params if provided
        if (picaServiceId) {
            params = { ...params, pica_service_id: picaServiceId }
        }

        // Process date range filters (same as fetchOrders)
        if (params.created_at_range) {
            const range = params.created_at_range
            if (range.start) params.created_at__gte = range.start
            if (range.end) params.created_at__lte = range.end
            delete params.created_at_range
        }

        if (params.paid_at_range) {
            const range = params.paid_at_range
            if (range.start) params.paid_at__gte = range.start
            if (range.end) params.paid_at__lte = range.end
            delete params.paid_at_range
        }

        // Remove pagination params for export (export all matching records)
        const exportParams = { ...params }
        delete exportParams.page
        delete exportParams.page_size

        // Remove null/empty values
        const cleanParams = Object.keys(exportParams).reduce((acc, key) => {
            if (exportParams[key] !== null && exportParams[key] !== '') {
                acc[key] = exportParams[key]
            }
            return acc
        }, {})

        return await gatewayClient.download(endpoints.exportCsv, cleanParams)
    },

    // Media from edge orders endpoint (Core)
    async fetchEdgeOrderMedia(picaServiceId, params, queryParams = {}) {
        const endpoint = endpoints.edgeOrderMedia.replace('{picaServiceId}', picaServiceId)

        // Build query string for pagination
        const queryString = new URLSearchParams({
            page: queryParams.page || 1,
            page_size: queryParams.page_size || 100  // Default to 100 for better performance
        }).toString()

        // POST request with picacode and optionally media_ids or gallery_ids
        // Include printable parameter if provided (for watermark control)
        const requestParams = { ...params }
        if (queryParams.printable !== undefined) {
            requestParams.printable = queryParams.printable
        }

        const response = await coreClient.post(`${endpoint}?${queryString}`, requestParams)

        // Return response as-is, no need to remap fields
        // API already provides thumb and sd fields

        return response
    },

    // Download media endpoint (Core)
    async downloadMedia(picaServiceId, params) {
        const endpoint = endpoints.downloadMedia.replace('{picaServiceId}', picaServiceId)

        // Build request body with printable parameter if provided
        const requestParams = { ...params }

        // Request the download URL from the API
        const response = await coreClient.post(endpoint, requestParams)

        // API returns {status: 'success', url: 'presigned_s3_url'}
        if (response.status === 'success' && response.url) {
            // Download using the downloader utility
            // Add suffix to filename based on printable status
            const suffix = params.printable === true ? 'printable' : params.printable === false ? 'digital' : 'photos'
            downloader.downloadFromUrl(response.url, `${suffix}_${params.picacode}_${Date.now()}.zip`)
        } else {
            throw new Error('Failed to generate download URL')
        }

        return response
    }
}